import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getLogger } from 'loglevel';

import type { CartId } from '@apple/features/cart/models/cart-id';

import { deleteSavedBulkOrder, saveBulkOrder, setActiveSavedBulkOrder } from '../api';
import type { SaveBulkOrderRequest } from '../models/models';
import { cartQueryKeys, cartQueryOptions } from '../queries';

const log = getLogger('useSavedCarts');

export function useSavedCarts() {
	const queryClient = useQueryClient();
	const savedCarts = useQuery(cartQueryOptions.saved);

	const activeCart = useMutation({
		mutationKey: ['cart', 'set-active'],
		mutationFn: async (cartId: CartId) => {
			log.debug('Setting cart as active', cartId);
			await setActiveSavedBulkOrder(cartId);
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: cartQueryKeys.cartId,
			});
		},
	});

	const saveCart = useMutation({
		mutationKey: ['cart', 'save'],
		mutationFn: async (request: SaveBulkOrderRequest) => {
			log.debug('Saving cart', request);
			await saveBulkOrder(request);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: cartQueryKeys.cartId,
				}),
				queryClient.invalidateQueries({
					queryKey: cartQueryKeys.saved,
				}),
			]);
		},
	});

	const deleteSavedCart = useMutation({
		mutationKey: ['cart', 'delete'],
		mutationFn: async (cartId: CartId) => {
			log.debug('Deleting cart', cartId);
			await deleteSavedBulkOrder(cartId);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: cartQueryKeys.cartId,
				}),
				queryClient.invalidateQueries({
					queryKey: cartQueryKeys.saved,
				}),
			]);
		},
	});

	return {
		savedCarts,
		activeCart,
		saveCart,
		deleteSavedCart,
	};
}
